import { useEffect, useState } from "react";
import { AdminNav } from "@/components/admin/AdminNav";
import { CrmTabs } from "@/components/admin/CrmTabs";
import { api, money } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const Stat = ({ label, value, testid, alert }) => (
  <div className={`border p-5 ${alert ? "border-red-400/40 bg-red-400/5" : "border-white/10"}`} data-testid={testid}>
    <div className="font-display font-bold text-2xl sm:text-3xl">{value}</div>
    <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-500 mt-1">{label}</div>
  </div>
);

export default function CrmDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/crm/dashboard").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white" data-testid="crm-dashboard">
      <AdminNav />
      <CrmTabs />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-display font-bold uppercase tracking-tight text-2xl sm:text-3xl mb-8">CRM Dashboard</h1>
        {!data ? (
          <p className="text-neutral-500 font-mono text-xs uppercase tracking-[0.25em]" data-testid="crm-loading">Loading...</p>
        ) : (
          <Reveal>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <Stat testid="stat-carriers-total" label="Carriers Total" value={data.carriers_total} />
              <Stat testid="stat-carriers-available" label="Available Now" value={data.carriers_available} />
              <Stat testid="stat-carriers-under-load" label="Under Load" value={data.carriers_under_load} />
              <Stat testid="stat-loads-total" label="Loads Total" value={data.loads_total} />
              <Stat testid="stat-gross-revenue" label="Gross Load Revenue" value={money(data.gross_revenue)} />
              <Stat testid="stat-dispatch-fees" label="Dispatch Fees" value={money(data.dispatch_fees)} />
              <Stat testid="stat-payments-received" label="Payments Received" value={money(data.payments_received)} />
              <Stat testid="stat-balances-owed" label="Balances Owed" value={money(data.balances_owed)} alert={data.balances_owed > 0} />
            </div>

            <div className="grid lg:grid-cols-2 gap-3 mt-3">
              <div className="border border-white/10 p-5" data-testid="overdue-followups-panel">
                <h2 className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-400 mb-4">
                  Overdue Follow-ups ({data.overdue_followups.length})
                </h2>
                {data.overdue_followups.length === 0 ? (
                  <p className="text-neutral-600 text-sm">Nothing overdue.</p>
                ) : (
                  <ul className="divide-y divide-white/5">
                    {data.overdue_followups.map((f) => (
                      <li key={f.id} className="py-3 flex justify-between gap-4" data-testid={`overdue-followup-${f.id}`}>
                        <div>
                          <a href={`/admin/crm/carriers/${f.carrier_id}`} className="font-semibold hover:underline">{f.carrier_name}</a>
                          <p className="text-neutral-400 text-sm">{f.next_action || f.note}</p>
                        </div>
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-red-400 whitespace-nowrap">
                          Due {f.due_date}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="border border-white/10 p-5" data-testid="expiring-documents-panel">
                <h2 className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-400 mb-4">
                  Expiring Documents — 30 Days ({data.expiring_documents.length})
                </h2>
                {data.expiring_documents.length === 0 ? (
                  <p className="text-neutral-600 text-sm">No documents expiring soon.</p>
                ) : (
                  <ul className="divide-y divide-white/5">
                    {data.expiring_documents.map((d) => (
                      <li key={d.id} className="py-3 flex justify-between gap-4" data-testid={`expiring-doc-${d.id}`}>
                        <div>
                          <a href={`/admin/crm/carriers/${d.carrier_id}`} className="font-semibold hover:underline">{d.carrier_name}</a>
                          <p className="text-neutral-400 text-sm capitalize">{d.doc_type} — {d.original_filename}</p>
                        </div>
                        <span className={`font-mono text-[10px] uppercase tracking-[0.2em] whitespace-nowrap ${d.expired ? "text-red-400" : "text-yellow-400"}`}>
                          {d.expired ? "Expired" : "Expires"} {d.expires_at}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </Reveal>
        )}
      </main>
    </div>
  );
}
